import { useEffect } from 'react'
import { App, Modal, type ModalFuncProps } from 'antd'

import { i18n } from '@/shared/i18n'

export type ConfirmPopupOptions = ModalFuncProps
export type ConfirmPopupHandle = ReturnType<typeof Modal.confirm>

type ConfirmFn = (options: ModalFuncProps) => ConfirmPopupHandle

/** A question left open longer than this no longer blocks a new one (missed close callback). */
const STALE_QUESTION_MS = 10 * 60 * 1000

let contextConfirm: ConfirmFn | null = null
let openQuestion: { handle: ConfirmPopupHandle; openedAt: number } | null = null

/**
 * Rendered once inside antd `<App>` (App.tsx): hands confirmPopup the App-context `modal`, so the
 * question uses the app's theme and locale like every other popup. The static `Modal.confirm`
 * cannot read that context; it is only the fallback before this mounts (e.g. in tests).
 */
export function PopupHost(): null {
  const { modal } = App.useApp()
  useEffect(() => {
    const confirm: ConfirmFn = (options) => modal.confirm(options)
    contextConfirm = confirm
    return () => {
      if (contextConfirm === confirm) contextConfirm = null
    }
  }, [modal])
  return null
}

/**
 * The dealer backoffice yes/no question (관리자 BO 194와 같은 모듈 — 개발항목 294). Every
 * "정말 …하시겠습니까?" goes through here instead of calling antd `Modal.confirm` directly. The
 * cancel button reads `common.buttons.cancel` unless the caller names it.
 *
 * Only one question is open at a time: a second call while one is open (a double click on
 * 거절·승인·발송 before the first question appeared) returns the open question instead of
 * stacking another one — the stacked one used to send the same request twice. Return the request's
 * promise from `onOk` so the OK button stays loading until the server answered.
 *
 * @returns antd's handle (`destroy`, `update`) for the opened question
 */
export function confirmPopup(options: ConfirmPopupOptions): ConfirmPopupHandle {
  const now = Date.now()
  if (openQuestion && now - openQuestion.openedAt < STALE_QUESTION_MS) {
    return openQuestion.handle
  }

  let handle: ConfirmPopupHandle | null = null
  const release = (): void => {
    if (openQuestion?.handle === handle) openQuestion = null
  }
  const confirm = contextConfirm ?? ((config: ModalFuncProps) => Modal.confirm(config))
  handle = confirm({
    cancelText: i18n.t('common.buttons.cancel'),
    ...options,
    onOk: (...args: unknown[]): unknown => {
      release()
      return options.onOk?.(...args)
    },
    onCancel: (...args: unknown[]): unknown => {
      release()
      return options.onCancel?.(...args)
    },
    afterClose: () => {
      release()
      options.afterClose?.()
    },
  })
  openQuestion = { handle, openedAt: now }
  return handle
}
